import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle2, CreditCard } from 'lucide-react'
import { Button, Card, Spinner } from '@/components/ui'
import { PageShell } from '@/components/common/PageShell'
import { useBilling } from '../hooks/useBilling'
import { describeStatus } from './statusCopy'

/** How long to keep asking before admitting the webhook has not arrived. */
const GIVE_UP_AFTER_MS = 30_000
const POLL_MS = 2_000

/**
 * Where Stripe sends the browser back after Checkout.
 *
 * It polls our own API rather than trusting the URL. Stripe redirects as soon
 * as the card is accepted, which is *before* the webhook that tells us a
 * subscription exists — so the page that reads `?session_id=` and declares
 * success is wrong perhaps a second out of every few. Everything here waits for
 * our own state to change.
 *
 * Refreshing is safe: there is nothing to submit and no state held in the page.
 */
export function BillingReturnPage(): React.ReactElement {
  const navigate = useNavigate()
  const [waitedTooLong, setWaitedTooLong] = useState(false)
  const { data } = useBilling({ pollMs: waitedTooLong ? undefined : POLL_MS })

  const settled = Boolean(data?.hasSubscription && data.status)

  useEffect(() => {
    if (settled) return
    const timer = setTimeout(() => setWaitedTooLong(true), GIVE_UP_AFTER_MS)
    return () => clearTimeout(timer)
  }, [settled])

  return (
    <PageShell
      icon={CreditCard}
      breadcrumbItems={[{ label: 'Billing', to: '/billing' }, { label: 'Confirming' }]}
      title={settled ? 'You are all set' : 'Confirming your subscription'}
      description={
        settled
          ? 'Your subscription is active and this workspace is up to date.'
          : 'Stripe has taken your details. We are waiting for confirmation.'
      }
    >
      <Card className="p-6">
        {settled && data ? (
          <div className="flex items-start gap-3">
            <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-success" aria-hidden />
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {data.planName} — {describeStatus(data).label}
              </p>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {describeStatus(data).detail}
              </p>
              <Button className="mt-4" onClick={() => navigate('/billing')}>
                Go to billing
              </Button>
            </div>
          </div>
        ) : waitedTooLong ? (
          <div>
            <p className="text-sm text-gray-900 dark:text-gray-100">
              This is taking longer than usual.
            </p>
            <p className="mt-1 max-w-prose text-sm text-gray-600 dark:text-gray-400">
              Your payment went through — Stripe has it. We have not had the
              confirmation yet, which usually means it is seconds away. Nothing has
              been charged twice, and refreshing is safe.
            </p>
            <div className="mt-4 flex gap-2">
              <Button variant="secondary" onClick={() => window.location.reload()}>
                Check again
              </Button>
              <Button variant="ghost" onClick={() => navigate('/billing')}>
                Go to billing
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <Spinner />
            <div>
              <p className="text-sm text-gray-900 dark:text-gray-100">
                Waiting for Stripe to confirm…
              </p>
              <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-400">
                This usually takes a couple of seconds. You can leave this page — the
                subscription completes either way.
              </p>
            </div>
          </div>
        )}
      </Card>
    </PageShell>
  )
}
